<template>
  <div class="gst-report">
    <header class="page-header">
      <button class="btn-back" @click="$router.go(-1)">←</button>
      <h1>{{ $t('wholesaler.GstReport.title') }}</h1>
      <button class="btn-export" @click="showExportDialog = true">{{ $t('wholesaler.GstReport.export') }}</button>
    </header>

    <div class="period-selector">
      <div class="selector-row">
        <label>{{ $t('wholesaler.GstReport.year') }}:</label>
        <select v-model="selectedYear" @change="loadReport">
          <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <div class="selector-row">
        <label>{{ $t('wholesaler.GstReport.quarter') }}:</label>
        <select v-model="selectedQuarter" @change="loadReport">
          <option :value="1">{{ $t('wholesaler.GstReport.q1') }}</option>
          <option :value="2">{{ $t('wholesaler.GstReport.q2') }}</option>
          <option :value="3">{{ $t('wholesaler.GstReport.q3') }}</option>
          <option :value="4">{{ $t('wholesaler.GstReport.q4') }}</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading">{{ $t('common.loading') }}</div>

    <div v-else class="report-content">
      <!-- 缺失数据提示 -->
      <div v-if="report.hasMissingMonths" class="section-card warning">
        <div class="section-title">{{ $t('wholesaler.GstReport.dataIncomplete') }}</div>
        <p class="hint-text">{{ getMissingDataHint() }}</p>
        <button class="btn-primary" @click="openHistoricalDialog">
          {{ $t('wholesaler.GstReport.supplementHistory') }}
        </button>
      </div>

      <!-- 申报信息 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.GstReport.declarationInfo') }}</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.GstReport.declarationPeriod') }}</span>
            <span class="value">{{ report.period }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.GstReport.gstRegNo') }}</span>
            <span class="value">{{ report.gstRegNo }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.GstReport.companyName') }}</span>
            <span class="value">{{ report.companyName }}</span>
          </div>
          <div class="info-item">
            <span class="label">{{ $t('wholesaler.GstReport.dueDate') }}</span>
            <span class="value warning">{{ report.dueDate }}</span>
          </div>
        </div>
      </div>

      <!-- GST F5 申报表 -->
      <div class="section-card highlight">
        <div class="section-title">{{ $t('wholesaler.GstReport.gstF5Form') }}</div>
        <div class="data-table">
          <div class="table-row">
            <span class="box-label">Box 1</span>
            <span class="label">{{ $t('wholesaler.GstReport.box1Label') }}</span>
            <span class="value">S$ {{ formatMoney(report.box1) }}</span>
          </div>
          <div class="table-row">
            <span class="box-label">Box 2</span>
            <span class="label">{{ $t('wholesaler.GstReport.box2Label') }}</span>
            <span class="value">S$ {{ formatMoney(report.box2) }}</span>
          </div>
          <div class="table-row">
            <span class="box-label">Box 3</span>
            <span class="label">{{ $t('wholesaler.GstReport.box3Label') }}</span>
            <span class="value">S$ {{ formatMoney(report.box3) }}</span>
          </div>
          <div class="table-row total">
            <span class="box-label">Box 4</span>
            <span class="label">{{ $t('wholesaler.GstReport.box4Label') }}</span>
            <span class="value">S$ {{ formatMoney(report.box4) }}</span>
          </div>
          <div class="table-row">
            <span class="box-label">Box 5</span>
            <span class="label">{{ $t('wholesaler.GstReport.box5Label') }}</span>
            <span class="value">S$ {{ formatMoney(report.box5) }}</span>
          </div>
          <div class="table-row">
            <span class="box-label">Box 6</span>
            <span class="label">{{ $t('wholesaler.GstReport.box6Label') }}</span>
            <span class="value">S$ {{ formatMoney(report.box6) }}</span>
          </div>
          <div class="table-row total highlight">
            <span class="box-label">Box 7</span>
            <span class="label">{{ $t('wholesaler.GstReport.box7Label') }}</span>
            <span class="value" :class="report.box7 >= 0 ? 'payable' : 'refundable'">
              S$ {{ formatMoney(Math.abs(report.box7)) }}
              <span class="tag">{{ report.box7 >= 0 ? $t('wholesaler.GstReport.payable') : $t('wholesaler.GstReport.refundable') }}</span>
            </span>
          </div>
        </div>
      </div>

      <!-- 销项税明细 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.GstReport.outputTaxDetails') }}</div>
        <div class="data-table">
          <div class="table-row" v-for="item in report.outputTaxDetails" :key="item.month">
            <span class="label">{{ item.month }}</span>
            <span class="sub-label">{{ $t('wholesaler.GstReport.sales') }}: S$ {{ formatMoney(item.sales) }}</span>
            <span class="value">S$ {{ formatMoney(item.tax) }}</span>
          </div>
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.GstReport.total') }}</span>
            <span class="sub-label"></span>
            <span class="value">S$ {{ formatMoney(report.box6) }}</span>
          </div>
        </div>
      </div>

      <!-- 进项税明细 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.GstReport.inputTaxDetails') }}</div>
        <div class="data-table">
          <div class="table-row" v-for="item in report.inputTaxDetails" :key="item.month">
            <span class="label">{{ item.month }}</span>
            <span class="sub-label">{{ $t('wholesaler.GstReport.purchases') }}: S$ {{ formatMoney(item.purchases) }}</span>
            <span class="value">S$ {{ formatMoney(item.tax) }}</span>
          </div>
          <div class="table-row total">
            <span class="label">{{ $t('wholesaler.GstReport.total') }}</span>
            <span class="sub-label"></span>
            <span class="value">S$ {{ formatMoney(report.box5) }}</span>
          </div>
        </div>
      </div>

      <!-- 汇报文案 -->
      <div class="section-card">
        <div class="section-title">{{ $t('wholesaler.GstReport.reportSummary') }}</div>
        <div class="report-text">
          <p><strong>{{ $t('wholesaler.GstReport.gstQuarterlySummary') }} ({{ report.period }})</strong></p>
          <p>{{ report.summary }}</p>
        </div>
        <button class="btn-copy" @click="copySummary">{{ $t('wholesaler.GstReport.copySummary') }}</button>
      </div>

      <!-- 重要提醒 -->
      <div class="section-card warning">
        <div class="section-title">{{ $t('wholesaler.GstReport.importantReminders') }}</div>
        <ul class="reminder-list">
          <li>{{ $t('wholesaler.GstReport.reminder1', { dueDate: report.dueDate }) }}</li>
          <li>{{ $t('wholesaler.GstReport.reminder2', { paymentDueDate: report.paymentDueDate }) }}</li>
          <li>{{ $t('wholesaler.GstReport.reminder3') }}</li>
          <li>{{ $t('wholesaler.GstReport.reminder4') }}</li>
        </ul>
      </div>
    </div>

    <!-- 导出对话框 -->
    <div v-if="showExportDialog" class="modal-overlay modal-show" @click="showExportDialog = false">
      <div class="modal-content" @click.stop>
        <h3>{{ $t('wholesaler.GstReport.exportReport') }}</h3>
        <div class="export-options">
          <button @click="exportReport('excel')" class="btn-option">
            <span class="icon">📊</span>
            <span>{{ $t('wholesaler.GstReport.excelFormat') }}</span>
          </button>
        </div>
        <button class="btn-cancel" @click="showExportDialog = false">{{ $t('wholesaler.GstReport.cancel') }}</button>
      </div>
    </div>

    <!-- 历史数据补充对话框 -->
    <div v-if="showHistoricalDialog" class="modal-overlay modal-show" @click="closeHistoricalDialog">
      <div class="modal-content modal-large" @click.stop>
        <h3>{{ $t('wholesaler.GstReport.supplementHistoryData', { quarter: selectedQuarter, year: selectedYear }) }}</h3>
        <p class="dialog-hint">{{ $t('wholesaler.GstReport.historyHint') }}</p>
        
        <div class="historical-form">
          <div class="month-section" v-for="month in quarterMonths" :key="month">
            <h4>{{ $t('wholesaler.GstReport.months')[month - 1] }}</h4>
            <div class="form-row">
              <div class="form-group">
                <label>{{ $t('wholesaler.GstReport.historicalSales') }}</label>
                <div class="input-with-prefix">
                  <span class="prefix">S$</span>
                  <input type="number" v-model="historicalData.monthlySales[month]" step="0.01" min="0" placeholder="0.00" />
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('wholesaler.GstReport.historicalPurchases') }}</label>
                <div class="input-with-prefix">
                  <span class="prefix">S$</span>
                  <input type="number" v-model="historicalData.monthlyPurchases[month]" step="0.01" min="0" placeholder="0.00" />
                </div>
              </div>
            </div>
          </div>
          <div class="form-group full-width">
            <label>{{ $t('wholesaler.GstReport.remark') }}</label>
            <textarea v-model="historicalData.remark" :placeholder="$t('wholesaler.GstReport.remarkPlaceholder')" rows="2"></textarea>
          </div>
        </div>

        <div class="dialog-actions">
          <button v-if="historicalDataId" class="btn-danger" @click="confirmDelete">{{ $t('wholesaler.GstReport.delete') }}</button>
          <div class="right-actions">
            <button class="btn-cancel" @click="closeHistoricalDialog">{{ $t('wholesaler.GstReport.cancel') }}</button>
            <button class="btn-primary" @click="saveHistoricalData" :disabled="saving">
              {{ saving ? $t('wholesaler.GstReport.saving') : $t('wholesaler.GstReport.save') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getGstQuarterlyReport, exportGstReport, getTaxHistoricalData, createTaxHistoricalData, deleteTaxHistoricalData } from '@/api/tax'
import { getWholesalerId } from '@/utils/wholesaler'

// 获取批发商ID的工具函数

export default {
  name: 'GstReport',
  data() {
    return {
      selectedYear: new Date().getFullYear(),
      selectedQuarter: Math.ceil((new Date().getMonth() + 1) / 3),
      years: [],
      report: {
        year: 0,
        quarter: 0,
        period: '',
        gstRegNo: '',
        companyName: '',
        dueDate: '',
        paymentDueDate: '',
        box1: 0,
        box2: 0,
        box3: 0,
        box4: 0,
        box5: 0,
        box6: 0,
        box7: 0,
        outputTaxDetails: [],
        inputTaxDetails: [],
        summary: ''
      },
      loading: false,
      showExportDialog: false,
      showHistoricalDialog: false,
      historicalDataId: null,
      saving: false,
      historicalData: {
        monthlySales: {},
        monthlyPurchases: {},
        remark: ''
      }
    }
  },
  computed: {
    quarterMonths() {
      const start = (this.selectedQuarter - 1) * 3 + 1
      return [start, start + 1, start + 2]
    }
  },
  created() {
    this.initYears()
    this.loadReport()
  },
  methods: {
    initYears() {
      const currentYear = new Date().getFullYear()
      this.years = Array.from({ length: 5 }, (_, i) => currentYear - i)
    },
    async loadReport() {
      this.loading = true
      try {
        const wholesalerId = getWholesalerId()
        const response = await getGstQuarterlyReport(
          this.selectedYear,
          this.selectedQuarter,
          wholesalerId
        )
        this.report = response.data
        this.generateSummary()
      } catch (error) {
        console.error('Load report failed:', error)
        this.$toast.error(this.$t('wholesaler.GstReport.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    formatMoney(amount) {
      if (!amount) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    async exportReport(format) {
      try {
        const wholesalerId = getWholesalerId()
        const lang = this.$i18n.locale
        const response = await exportGstReport(
          this.selectedYear,
          this.selectedQuarter,
          wholesalerId,
          format,
          lang
        )
        const blob = new Blob([response.data], { type: response.headers['content-type'] })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const isZh = lang.startsWith('zh')
        const fileName = isZh 
          ? `GST报表_${this.selectedYear}_Q${this.selectedQuarter}.xlsx`
          : `GST_Report_${this.selectedYear}_Q${this.selectedQuarter}.xlsx`
        link.download = fileName
        link.click()
        window.URL.revokeObjectURL(url)
        this.showExportDialog = false
        this.$toast.success(this.$t('wholesaler.GstReport.exportSuccess'))
      } catch (error) {
        console.error('Export failed:', error)
        this.$toast.error(this.$t('wholesaler.GstReport.exportFailed'))
      }
    },
    generateSummary() {
      const year = this.selectedYear
      const quarter = this.selectedQuarter
      const period = this.report.period
      const isZh = this.$i18n.locale.startsWith('zh')
      
      const box1 = this.formatMoney(this.report.box1)
      const box2 = this.formatMoney(this.report.box2)
      const box3 = this.formatMoney(this.report.box3)
      const box4 = this.formatMoney(this.report.box4)
      const box5 = this.formatMoney(this.report.box5)
      const box6 = this.formatMoney(this.report.box6)
      const box7 = this.formatMoney(this.report.box7)
      
      if (isZh) {
        const box7Status = this.report.box7 >= 0 
          ? `应缴 ${box7}` 
          : `可退 ${this.formatMoney(Math.abs(this.report.box7))}`
        
        this.report.summary = [
          `GST F5 申报表 - ${period}`,
          ``,
          `供应额:`,
          `  Box 1 (标准税率): S$ ${box1}`,
          `  Box 2 (零税率): S$ ${box2}`,
          `  Box 3 (豁免): S$ ${box3}`,
          `  Box 4 (总计): S$ ${box4}`,
          ``,
          `税额计算:`,
          `  Box 5 (进项税): S$ ${box5}`,
          `  Box 6 (销项税): S$ ${box6}`,
          `  Box 7 (净 GST ${box7Status}): S$ ${this.formatMoney(Math.abs(this.report.box7))}`,
          ``,
          `申报截止: ${this.report.dueDate}`,
          `缴款截止: ${this.report.paymentDueDate}`
        ].join('\n')
      } else {
        const box7Status = this.report.box7 >= 0 
          ? `payable ${box7}` 
          : `refundable ${this.formatMoney(Math.abs(this.report.box7))}`
        
        this.report.summary = [
          `GST F5 Return - ${period}`,
          ``,
          `Supplies:`,
          `  Box 1 (Standard-rated): S$ ${box1}`,
          `  Box 2 (Zero-rated): S$ ${box2}`,
          `  Box 3 (Exempt): S$ ${box3}`,
          `  Box 4 (Total): S$ ${box4}`,
          ``,
          `Tax Calculation:`,
          `  Box 5 (Input tax): S$ ${box5}`,
          `  Box 6 (Output tax): S$ ${box6}`,
          `  Box 7 (Net GST ${box7Status}): S$ ${this.formatMoney(Math.abs(this.report.box7))}`,
          ``,
          `Filing deadline: ${this.report.dueDate}`,
          `Payment deadline: ${this.report.paymentDueDate}`
        ].join('\n')
      }
    },
    copySummary() {
      const text = this.report.summary
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success(this.$t('wholesaler.GstReport.copySuccess'))
      }).catch(() => {
        this.$toast.error(this.$t('wholesaler.GstReport.copyFailed'))
      })
    },
    getMissingDataHint() {
      const monthNames = this.$t('wholesaler.GstReport.months')
      const missingMonths = this.report.missingMonths || []
      if (missingMonths.length === 0) return ''
      const monthList = missingMonths.map(m => monthNames[m - 1]).join(', ')
      return this.$t('wholesaler.GstReport.missingDataHint', { months: monthList })
    },
    async openHistoricalDialog() {
      this.showHistoricalDialog = true
      try {
        const wholesalerId = getWholesalerId()
        const response = await getTaxHistoricalData(wholesalerId, this.selectedYear, this.selectedQuarter)
        if (response.data && response.data.id) {
          this.historicalDataId = response.data.id
          // 初始化所有月份为 0，然后用返回的数据覆盖
          const monthlySales = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 }
          const monthlyPurchases = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 }
          if (response.data.monthlySales) {
            Object.assign(monthlySales, response.data.monthlySales)
          }
          if (response.data.monthlyPurchases) {
            Object.assign(monthlyPurchases, response.data.monthlyPurchases)
          }
          this.historicalData = {
            monthlySales,
            monthlyPurchases,
            remark: response.data.remark || ''
          }
        } else {
          // 初始化所有月份为 0
          const monthlySales = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 }
          const monthlyPurchases = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0, 10: 0, 11: 0, 12: 0 }
          this.historicalData = {
            monthlySales,
            monthlyPurchases,
            remark: ''
          }
        }
      } catch (error) {
        console.error('Load historical data failed:', error)
      }
    },
    closeHistoricalDialog() {
      this.showHistoricalDialog = false
      this.historicalDataId = null
      this.historicalData = {
        monthlySales: {},
        monthlyPurchases: {},
        remark: ''
      }
    },
    async saveHistoricalData() {
      this.saving = true
      try {
        const wholesalerId = getWholesalerId()
        const data = {
          wholesalerId: Number(wholesalerId),
          year: this.selectedYear,
          quarter: this.selectedQuarter,
          monthlySales: this.historicalData.monthlySales,
          monthlyPurchases: this.historicalData.monthlyPurchases,
          remark: this.historicalData.remark
        }
        await createTaxHistoricalData(data)
        this.$toast.success(this.$t('wholesaler.GstReport.saveSuccess'))
        this.closeHistoricalDialog()
        this.loadReport()
      } catch (error) {
        console.error('Save failed:', error)
        this.$toast.error(this.$t('wholesaler.GstReport.saveFailed'))
      } finally {
        this.saving = false
      }
    },
    async confirmDelete() {
      if (!confirm(this.$t('wholesaler.GstReport.confirmDelete'))) return
      try {
        await deleteTaxHistoricalData(this.historicalDataId)
        this.$toast.success(this.$t('wholesaler.GstReport.deleteSuccess'))
        this.closeHistoricalDialog()
        this.loadReport()
      } catch (error) {
        console.error('Delete failed:', error)
        this.$toast.error(this.$t('wholesaler.GstReport.deleteFailed'))
      }
    }
  }
}
</script>

<style scoped>
.gst-report {
  padding: 16px;
  background: #f5f5f5;
  min-height: 100vh;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  margin-bottom: 16px;
}

.btn-back {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
}

.page-header h1 {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.btn-export {
  background: #1890ff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.period-selector {
  background: white;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
}

.selector-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selector-row label {
  font-weight: 500;
  white-space: nowrap;
}

.selector-row select {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  flex: 1;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #999;
}

.report-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
}

.section-card.highlight {
  background: #e6f7ff;
  border: 1px solid #91d5ff;
}

.section-card.warning {
  background: #fff7e6;
  border: 1px solid #ffd591;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #333;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item .label {
  font-size: 12px;
  color: #666;
}

.info-item .value {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.info-item .value.warning {
  color: #fa8c16;
}

.data-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.table-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.table-row.total {
  border-bottom: none;
  border-top: 2px solid #333;
  margin-top: 8px;
  padding-top: 12px;
  font-weight: 600;
  font-size: 15px;
}

.table-row.total.highlight {
  background: #fff;
  padding: 12px 8px;
  border-radius: 4px;
  margin-top: 12px;
}

.box-label {
  background: #1890ff;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  min-width: 50px;
  text-align: center;
}

.table-row .label {
  flex: 1;
  color: #666;
  font-size: 14px;
}

.table-row .sub-label {
  color: #999;
  font-size: 12px;
  min-width: 120px;
  text-align: right;
}

.table-row .value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
  min-width: 100px;
  text-align: right;
}

.table-row.total .value {
  font-size: 16px;
  color: #1890ff;
}

.table-row.total .value.payable {
  color: #f5222d;
}

.table-row.total .value.refundable {
  color: #52c41a;
}

.tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
  margin-left: 4px;
}

.value.payable .tag {
  background: #fff1f0;
  color: #f5222d;
}

.value.refundable .tag {
  background: #f6ffed;
  color: #52c41a;
}

.report-text {
  background: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;
}

.report-text p {
  margin: 8px 0;
}

.btn-copy {
  background: #52c41a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  width: 100%;
}

.reminder-list {
  margin: 0;
  padding-left: 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.8;
}

.reminder-list li {
  margin-bottom: 8px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
}

.modal-content h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
}

.export-options {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.btn-option {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.btn-option:hover {
  border-color: #1890ff;
  background: #e6f7ff;
}

.btn-option .icon {
  font-size: 32px;
}

.btn-cancel {
  width: 100%;
  padding: 10px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}

.btn-cancel:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.btn-primary {
  background: #1890ff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
}

.btn-primary:hover {
  background: #40a9ff;
}

.btn-danger {
  background: #ff4d4f;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-danger:hover {
  background: #ff7875;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-overlay.modal-show {
  display: flex;
}

.modal-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content.modal-large {
  max-width: 600px;
}

.modal-content h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
}

.dialog-hint {
  color: #666;
  font-size: 14px;
  margin-bottom: 16px;
  line-height: 1.6;
}

.hint-text {
  color: #fa8c16;
  font-size: 14px;
  margin: 8px 0;
  line-height: 1.6;
}

.historical-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.input-with-prefix {
  display: flex;
  align-items: center;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;
}

.input-with-prefix:focus-within {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.input-with-prefix .prefix {
  padding: 8px 12px;
  background: #f5f5f5;
  color: #666;
  font-size: 14px;
  border-right: 1px solid #d9d9d9;
}

.input-with-prefix input {
  flex: 1;
  border: none;
  padding: 8px 12px;
  font-size: 14px;
  outline: none;
}

.form-group textarea {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;
}

.form-group textarea:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.dialog-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
}

.right-actions {
  display: flex;
  gap: 12px;
  margin-left: auto;
}

.right-actions .btn-cancel,
.right-actions .btn-primary {
  min-width: 80px;
  margin-top: 0;
}
</style>
